import { AppLocation } from 'src/navigation/appLocations'
import * as Routes from 'src/navigation/urls'

/**
 * Accounts flat route tree (EX-39923, supersedes #6986) — mirrors the landed
 * Budgets tabbed-family pattern (`budgetsRouteTree`) as the single source that
 * describes the Accounts tab tree (family paths + entity sub-tabs).
 *
 * Unlike `budgetsRouteTree`, this tree is NOT fully consumed by the page
 * components yet: only the entity `:id` redirect target
 * (`accountsRouteTree.entity.redirectTo`) is read directly by
 * `AccountEntityPage`. The rest derives from it:
 *   - the flat-route GATE paths (`accountsFamilyGatePaths` / `accountsEntityGatePaths`),
 *   - the per-path route METADATA fed to `locationHooks` (sidenav active state,
 *     breadcrumbs, content type) — built in `flatArchitectureRouteMetadata.ts`.
 *   `AccountsFamilyPage`'s nested `<Switch>` and `AccountEntityPage`'s nested
 *   `<Switch>` + Subnav tab list hardcode their own paths/tabs (parity with the
 *   legacy shells' conditional tab logic) rather than mapping over this tree's
 *   `tabs` arrays — keep them in sync with this file by hand when either
 *   changes; `accountsRouteTree.test.ts` only guards the gate paths + metadata,
 *   not the page components' own route lists.
 *
 * Two shapes, two gate strategies (see the gate-path builders below):
 *   - Family paths are STATIC (`/accounts/youraccounts`, …). Enumerated in the
 *     gate so each out-specifies the entity `:id` route (`/accounts/:id`) — the
 *     family entry MUST be registered before the entity entry so a static
 *     segment beats a param at equal depth (source order in the one-switch).
 *   - Entity sub-tabs all live under `/accounts/:id`. The gate matches them with
 *     a single param-child pattern (`/accounts/:id/:accountTab`) so BOTH known
 *     tabs and unknown children resolve to the entity page, preserving the
 *     entity-scoped not-found fallback the legacy nested `<Switch>` produced.
 *
 * Parity note — the Accounts family is NOT a tabbed surface. Unlike Budgets, the
 * legacy `Accounts` shell renders a "Physical Cards" title with NO tab bar; the
 * family paths here exist ONLY to feed the gate + metadata. `AccountsFamilyPage`
 * preserves that no-tab rendering (it does not mount a FamilyLayout tab bar).
 */

export interface AccountsFamilyTabNode {
  /** Stable key used for active-tab detection. */
  key: string
  /** Translation key; rendered with <Trns/> in the page. */
  labelSource: string
  /** AppLocation that drives sidenav active state via locationHooks. */
  appLocation: AppLocation
  /** Exact URL pattern, e.g. `/accounts/youraccounts`. */
  path: string
}

export interface AccountsEntityTabNode {
  key: string
  labelSource: string
  appLocation: AppLocation
  /** Exact URL pattern with the `:id` param, e.g. `/accounts/:id/overview`. */
  path: string
  /** Builds the navigable URL for a concrete account id. */
  to: (id: string) => string
  /**
   * Whether this route can surface as a tab in the entity Subnav. `history` is a
   * route with NO tab (`false`); the other five are tab CANDIDATES whose FINAL
   * visibility is gated at runtime by card features/permissions (physical-only,
   * closed, budgets-enabled) in `AccountEntityPage` — parity with the legacy
   * `Account` shell's conditional tab set.
   */
  showInTabBar: boolean
}

export const accountsRouteTree = {
  family: {
    /** `/accounts` — the family page redirects it to the first available tab. */
    rootPath: Routes.accountsUrl(),
    rootAppLocation: AppLocation.Accounts,
    // All family paths map to AppLocation.Accounts (the family is one location,
    // not per-tab locations like Budgets). No FamilyLayout tab bar is rendered.
    tabs: [
      {
        key: 'youraccounts',
        labelSource: 'Your Accounts',
        appLocation: AppLocation.Accounts,
        path: Routes.accountsAccountsUrl(),
      },
      {
        key: 'budgets',
        labelSource: 'Budgets',
        appLocation: AppLocation.Accounts,
        path: Routes.accountsBudgetsUrl(),
      },
      {
        key: 'virtualcards',
        labelSource: 'Virtual Cards',
        appLocation: AppLocation.Accounts,
        path: Routes.accountsVirtualCardsUrl(),
      },
      {
        key: 'requests',
        labelSource: 'Requests',
        appLocation: AppLocation.Accounts,
        path: Routes.accountsRequestsUrl(),
      },
    ] as AccountsFamilyTabNode[],
  },
  entity: {
    /** `/accounts/:id` — redirects to the first sub-tab (overview). */
    rootPath: Routes.accountUrl(':id'),
    rootAppLocation: AppLocation.Account,
    /** Param name used by the gate's param-child matcher. */
    tabParam: 'accountTab',
    redirectTo: (id: string) => Routes.accountOverviewUrl(id),
    // Display order matches the legacy `Account` Subnav: overview, budgets,
    // virtualcards, transactions, settings. `history` is routable but tab-less.
    tabs: [
      {
        key: 'overview',
        labelSource: 'Overview',
        appLocation: AppLocation.AccountOverview,
        path: Routes.accountOverviewUrl(':id'),
        to: Routes.accountOverviewUrl,
        showInTabBar: true,
      },
      {
        key: 'budgets',
        labelSource: 'Budgets',
        appLocation: AppLocation.AccountBudgets,
        path: Routes.accountBudgetsUrl(':id'),
        to: Routes.accountBudgetsUrl,
        showInTabBar: true,
      },
      {
        key: 'virtualcards',
        labelSource: 'Virtual Cards',
        appLocation: AppLocation.AccountVirtualCards,
        path: Routes.accountVirtualCardsUrl(':id'),
        to: Routes.accountVirtualCardsUrl,
        showInTabBar: true,
      },
      {
        key: 'transactions',
        labelSource: 'Transactions',
        appLocation: AppLocation.AccountTransactions,
        path: Routes.accountTransactionsUrl(':id'),
        to: Routes.accountTransactionsUrl,
        showInTabBar: true,
      },
      {
        key: 'settings',
        labelSource: 'Account Settings',
        appLocation: AppLocation.AccountSettings,
        path: Routes.accountSettingsUrl(':id'),
        to: Routes.accountSettingsUrl,
        showInTabBar: true,
      },
      {
        key: 'history',
        labelSource: 'History',
        appLocation: AppLocation.AccountBudgetHistory,
        path: Routes.accountHistoryUrl(':id'),
        to: Routes.accountHistoryUrl,
        showInTabBar: false,
      },
    ] as AccountsEntityTabNode[],
  },
}

/**
 * GATE paths for the family page: the family root plus every static tab path.
 * Enumerated (not parameterized) so each static path out-specifies the entity
 * `/accounts/:id` route at equal depth. The family gate entry MUST be registered
 * before the entity gate entry (source order in the one-switch).
 */
export const accountsFamilyGatePaths: string[] = [
  accountsRouteTree.family.rootPath,
  ...accountsRouteTree.family.tabs.map((t) => t.path),
]

/**
 * GATE paths for the entity page: the entity root plus a single param-child
 * matcher covering all sub-tabs (and unknown children, for fallback parity).
 *
 * Parity note (identical to Budgets): this matches the 2-segment entity root and
 * the 3-segment `/accounts/:id/<tab>` (including unknown tabs → entity-scoped
 * NotFound). A malformed DEEPER URL (`/accounts/:id/<tab>/<extra>`, 4+ segments)
 * no longer matches and falls to the global not-found instead of the legacy
 * in-page entity NotFound. No app-generated link produces such URLs, so this is
 * an accepted, documented delta rather than widening the matcher (which would
 * risk the static-beats-param specificity the family gate depends on).
 */
export const accountsEntityGatePaths: string[] = [
  accountsRouteTree.entity.rootPath,
  `${accountsRouteTree.entity.rootPath}/:${accountsRouteTree.entity.tabParam}`,
]
